import React, { useCallback, useMemo, useState } from "react";
import { Button, Input, Typography, message } from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { fetcher } from "../../utils/fetcher";
import { SingleCounsellingData } from "../../types/WellnessCounsellingTypes";
import {
  isCompletedSession,
  isPendingSessionNotes,
} from "./recentSessionsNotes";
import RecentSessionsTable from "./RecentSessionsTable";
import "./recentSessions.scss";

dayjs.extend(isBetween);

const { Text } = Typography;

type DatePreset = "today" | "7" | "30";

interface BookedSessionResponse {
  success: boolean;
  lastMonthBookedSessions: number;
  totalSessionCount: number;
  data: SingleCounsellingData[];
}

function resolveRange(preset: DatePreset): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
  const end = dayjs().endOf("day");
  switch (preset) {
    case "today":
      return { start: dayjs().startOf("day"), end };
    case "7":
      return { start: dayjs().subtract(6, "day").startOf("day"), end };
    case "30":
      return { start: dayjs().subtract(29, "day").startOf("day"), end };
    default:
      return { start: dayjs().subtract(29, "day").startOf("day"), end };
  }
}

function bylineForPreset(preset: DatePreset): string {
  switch (preset) {
    case "today":
      return "Showing sessions scheduled for today.";
    case "7":
      return "Showing sessions from the last 7 days.";
    case "30":
      return "Showing sessions from the last 30 days.";
    default:
      return "Showing sessions from the last 30 days.";
  }
}

const DATE_PRESETS: { id: DatePreset; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
];

function RecentSessions() {
  const listKey = `${process.env.REACT_APP_API_URL}/session/bookedSessionWithFlags/`;

  const [datePreset, setDatePreset] = useState<DatePreset>("30");
  const [notesScope, setNotesScope] = useState<"all" | "pending">("all");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
  const [search, setSearch] = useState("");

  const { data: sessionRes, isLoading, isValidating, mutate } =
    useSWR<BookedSessionResponse>(listKey, fetcher, {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: true,
    });

  const rawRows = sessionRes?.data ?? [];

  const { dateFiltered, pendingInRange, visibleRows } = useMemo(() => {
    const completedOnly = rawRows.filter((r) => isCompletedSession(r));
    const { start, end } = resolveRange(datePreset);
    const dateFiltered = completedOnly.filter(
      (r) => r.date && dayjs(r.date).isBetween(start, end, "day", "[]")
    );
    const pendingInRange = dateFiltered.filter((r) =>
      isPendingSessionNotes(r)
    ).length;
    let visible =
      notesScope === "pending"
        ? dateFiltered.filter((r) => isPendingSessionNotes(r))
        : dateFiltered;
    visible = [...visible].sort((a, b) => {
      const da = dayjs(a.date).valueOf();
      const db = dayjs(b.date).valueOf();
      return sortOrder === "desc" ? db - da : da - db;
    });
    return { dateFiltered, pendingInRange, visibleRows: visible };
  }, [rawRows, datePreset, notesScope, sortOrder]);

  const pendingInView = useMemo(
    () => visibleRows.filter((r) => isPendingSessionNotes(r)).length,
    [visibleRows]
  );

  const handleRefreshNow = useCallback(async () => {
    await mutate();
    message.open({
      type: "info",
      content: "Schedule refreshed",
      duration: 2,
    });
  }, [mutate]);

  const byline = bylineForPreset(datePreset);

  const showAllSessionsButton = (
    <Link to="/helpline_history">
      <Button type="default" className="recentSessions__ctaButton">
        Show all sessions
      </Button>
    </Link>
  );

  return (
    <div className="contentBox recentSessions">
      <header className="recentSessions__hero">
        <div>
          <div className="recentSessions__eyebrow">Counseling Management</div>
          <h1 className="recentSessions__title">Recent Sessions</h1>
          <p className="recentSessions__byline">{byline}</p>
        </div>
        <div className="recentSessions__heroActions">
          <Button onClick={handleRefreshNow} type="text">
            Refresh now
          </Button>
          {showAllSessionsButton}
        </div>
      </header>

      <section className="recentSessions__summary" aria-label="Summary">
        <div className="recentSessions__summaryCard">
          <div className="recentSessions__summaryValue">{dateFiltered.length}</div>
          <div className="recentSessions__summaryLabel">
            Completed sessions in range
          </div>
        </div>
        <div className="recentSessions__summaryCard recentSessions__summaryCard--accent">
          <div className="recentSessions__summaryValue">{pendingInView}</div>
          <div className="recentSessions__summaryLabel">Pending notes in view</div>
        </div>
      </section>

      <section className="recentSessions__filters" aria-label="Filters">
        <div className="recentSessions__filterBlock">
          <Text className="recentSessions__filterLabel">Date range</Text>
          <div className="recentSessions__chipRow">
            {DATE_PRESETS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                className={`recentSessions__pill recentSessions__pill--date${
                  datePreset === id ? " recentSessions__pill--active" : ""
                }`}
                onClick={() => {
                  setDatePreset(id);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="recentSessions__filterRow">
          <div className="recentSessions__filterBlock">
            <Text className="recentSessions__filterLabel">Notes</Text>
            <div className="recentSessions__chipRow">
              <button
                type="button"
                className={`recentSessions__pill${
                  notesScope === "all" ? " recentSessions__pill--active" : ""
                }`}
                onClick={() => setNotesScope("all")}
              >
                All ({dateFiltered.length})
              </button>
              <button
                type="button"
                className={`recentSessions__pill${
                  notesScope === "pending"
                    ? " recentSessions__pill--active"
                    : ""
                }`}
                onClick={() => setNotesScope("pending")}
              >
                Pending ({pendingInRange})
              </button>
            </div>
          </div>

          <div className="recentSessions__filterBlock">
            <Text className="recentSessions__filterLabel">Sort by date</Text>
            <div className="recentSessions__chipRow">
              <button
                type="button"
                className={`recentSessions__pill${
                  sortOrder === "desc" ? " recentSessions__pill--active" : ""
                }`}
                onClick={() => setSortOrder("desc")}
              >
                Newest first
              </button>
              <button
                type="button"
                className={`recentSessions__pill${
                  sortOrder === "asc" ? " recentSessions__pill--active" : ""
                }`}
                onClick={() => setSortOrder("asc")}
              >
                Oldest first
              </button>
            </div>
          </div>

          <div className="recentSessions__filterBlock recentSessions__filterBlock--grow">
            <Text className="recentSessions__filterLabel">Search</Text>
            <Input.Search
              className="searchField"
              placeholder="Search here..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {isValidating && !isLoading && (
            <div className="recentSessions__liveBadge">
              <Text type="secondary" className="recentSessions__syncUpdating">
                Updating…
              </Text>
            </div>
          )}
        </div>
      </section>

      <section className="recentSessions__tableShell" aria-label="Recent sessions table">
        {isValidating && !isLoading && <div className="recentSessions__syncBar" />}
        <div className="recentSessions__tableWrap">
          <div className="recentSessions__tableHeader">
            <h2 className="recentSessions__tableTitle">Completed sessions</h2>
            <Text type="secondary" className="recentSessions__tableCount">
              {visibleRows.length}
              {search.trim() ? " matching search" : " in view"}
            </Text>
          </div>
          <RecentSessionsTable
            rows={visibleRows}
            loading={isLoading}
            search={search}
          />
        </div>
      </section>

      <div className="recentSessions__footerCta">{showAllSessionsButton}</div>
    </div>
  );
}

export default RecentSessions;
